import { NavLink } from 'react-router-dom'
import { ChevronLeft, ChevronRight, MonitorSmartphone } from 'lucide-react'

import { Button, Separator } from '@/components/ui'
import { useIsMobile } from '@/hooks'
import { useSidebarStore } from '@/stores'
import { APP_NAME, APP_SHORT_NAME, navigationItems } from '@/utils/navigation'
import { cn } from '@/utils'

export function Sidebar() {
  const collapsed = useSidebarStore((s) => s.collapsed)
  const mobileOpen = useSidebarStore((s) => s.mobileOpen)
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed)
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen)
  const isMobile = useIsMobile()

  const widthClass = collapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]'

  return (
    <>
      {isMobile && mobileOpen ? (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border fixed inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-300',
          isMobile
            ? cn('w-[var(--sidebar-width)]', mobileOpen ? 'translate-x-0' : '-translate-x-full')
            : widthClass,
        )}
      >
        <div className={cn('flex h-[var(--header-height)] items-center gap-3 px-4', collapsed && !isMobile && 'justify-center px-2')}>
          <div className="bg-primary text-primary-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
            <MonitorSmartphone className="h-5 w-5" />
          </div>
          {(!collapsed || isMobile) && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight">{APP_SHORT_NAME}</p>
              <p className="text-muted-foreground truncate text-[11px]">Smart Budget</p>
            </div>
          )}
        </div>

        <Separator />

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {navigationItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === '/'}
                onClick={() => isMobile && setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    collapsed && !isMobile && 'justify-center px-2',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                  )
                }
                title={collapsed && !isMobile ? item.title : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {(!collapsed || isMobile) && <span className="truncate">{item.title}</span>}
              </NavLink>
            )
          })}
        </nav>

        <div className="border-sidebar-border border-t p-3">
          {!isMobile && (
            <Button
              variant="ghost"
              size={collapsed ? 'icon' : 'sm'}
              className={cn('w-full', !collapsed && 'justify-between')}
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {!collapsed && <span className="text-muted-foreground text-xs">Recolher</span>}
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          )}
          {(!collapsed || isMobile) && (
            <p className="text-muted-foreground mt-2 truncate px-1 text-[10px]">{APP_NAME}</p>
          )}
        </div>
      </aside>
    </>
  )
}
